import type { IEntity, IPoint } from "dxf-parser";
import type { IGroup } from "dxf-parser/dist/DxfArrayScanner";
import type DxfArrayScanner from "dxf-parser/dist/DxfArrayScanner";
import type IGeometry from "dxf-parser/dist/entities/geomtry";
import * as helpers from "lib/house/dxf/import/dxfParserLibHelpers";

type LwpolylineVertex = IPoint & {
  startWidth?: number;
  endWidth?: number;
  bulge?: number;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface ILwpolylineEntity extends IEntity {
  vertices: LwpolylineVertex[];
  elevation?: number;
  depth?: number;
  shape?: boolean;
  hasContinuousLinetypePattern?: boolean;
  width?: number;
  extrusionDirectionX?: number;
  extrusionDirectionY?: number;
  extrusionDirectionZ?: number;
}

export default class Lwpolyline implements IGeometry {
  public ForEntityName = "LWPOLYLINE" as any;

  public parseEntity(scanner: DxfArrayScanner, curr: IGroup) {
    const entity = {
      type: curr.value,
      vertices: [] as LwpolylineVertex[],
    } as ILwpolylineEntity;
    let numberOfVertices = 0;
    curr = scanner.next();
    while (!scanner.isEOF()) {
      if (curr.code === 0) {
        break;
      }

      switch (curr.code) {
        case 38: // Elevation
          entity.elevation = curr.value as number;
          break;
        case 39: // Thickness
          entity.depth = curr.value as number;
          break;
        case 70: // Polyline flag (1 = closed, 128 = plinegen)
          entity.shape = ((curr.value as number) & 1) === 1;
          entity.hasContinuousLinetypePattern = ((curr.value as number) & 128) === 128;
          break;
        case 90: // Number of vertices
          numberOfVertices = curr.value as number;
          break;
        case 10: // X coordinate of vertex (starts a vertex run)
          entity.vertices.push(...parseLwpolylineVertices(remainingVertexCount(entity, numberOfVertices), scanner));
          break;
        case 43: // Constant width
          if (curr.value !== 0) {
            entity.width = curr.value as number;
          }
          break;
        case 210: // X coordinate of extrusion direction
          entity.extrusionDirectionX = curr.value as number;
          break;
        case 220: // Y coordinate of extrusion direction
          entity.extrusionDirectionY = curr.value as number;
          break;
        case 230: // Z coordinate of extrusion direction
          entity.extrusionDirectionZ = curr.value as number;
          break;
        default:
          helpers.checkCommonEntityProperties(entity, curr, scanner);
          break;
      }
      curr = scanner.next();
    }
    return entity;
  }
}

function remainingVertexCount(entity: ILwpolylineEntity, numberOfVertices: number): number {
  return Math.max(0, numberOfVertices - entity.vertices.length);
}

function parseLwpolylineVertices(n: number, scanner: DxfArrayScanner): LwpolylineVertex[] {
  if (n <= 0) {
    return [];
  }
  const vertices: LwpolylineVertex[] = [];
  let vertexIsStarted = false;
  let vertexIsFinished = false;
  let curr = scanner.lastReadGroup;

  for (let i = 0; i < n; i++) {
    const vertex = {} as LwpolylineVertex;
    while (!scanner.isEOF()) {
      if (curr.code === 0 || vertexIsFinished) {
        break;
      }

      switch (curr.code) {
        case 10: // X coordinate of vertex
          if (vertexIsStarted) {
            vertexIsFinished = true;
            continue;
          }
          vertex.x = curr.value as number;
          vertexIsStarted = true;
          break;
        case 20: // Y coordinate of vertex
          vertex.y = curr.value as number;
          break;
        case 30: // Z coordinate of vertex
          vertex.z = curr.value as number;
          break;
        case 40: // Starting width of vertex
          vertex.startWidth = curr.value as number;
          break;
        case 41: // End width of vertex
          vertex.endWidth = curr.value as number;
          break;
        case 42: // Bulge of vertex
          if (curr.value !== 0) {
            vertex.bulge = curr.value as number;
          }
          break;
        default:
          scanner.rewind();
          if (vertexIsStarted && vertex.y !== undefined) {
            vertices.push(vertex);
          }
          return vertices;
      }
      curr = scanner.next();
    }
    if (vertexIsStarted && vertex.y !== undefined) {
      vertices.push(vertex);
    }
    vertexIsStarted = false;
    vertexIsFinished = false;
  }
  scanner.rewind();
  return vertices;
}
